/* global CKEDITOR */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import DrawingToolCkeditorWrapper from './plugins/drawing/DrawingToolCkeditorModal';
import DrawingDeleteConfirmationModal from './plugins/drawing/DrawingConfirmDeleteModal';
import MathEntryToolCkeditorModal from './plugins/mathEntry/MathEntryToolModal';
import registerDrawingPlugin from './plugins/drawing/DrawingPlugin';
import registerFixedPositionPlugin from './plugins/fixed/FixedPositionPlugin';
import mathEntryPlugin from './plugins/mathEntry/MathEntryPlugin';
import expandPlugin from './plugins/expand/ExpandPlugin';
import snapshotsPlugin from './plugins/snapshots/SnapshotsPlugin';
import SnapshotsModal from './plugins/snapshots/SnapshotsModal';
import getEditorConfig from './config/mainConfig';
import spellcheckerConfig from './config/spellcheckerConfig';
import wordCountPlugin from './plugins/wordcount/wordCountPlugin';
import domService from '../../../../../services/DomService';
import stringUtils from '../../../../../qti/utils/StringUtils';
import storeManager from '../../../../../store/StoreManager';
import i18nService, { t } from '../../../../../services/i18nService';

import { reportError } from '../../../../../services/ErrorReporting';
import sessionReader from '../../../../../store/readHelpers/SessionReader';
import questionReader from '../../../../../store/readHelpers/QuestionReader';

import settingsReader from '../../../../../store/readHelpers/SettingsReader';
import styles from './ckeditorWrapper.scss';
import CKEditorLoader from '../../../../../loading/resources/libraries/CKEDitor';
import alertService from '../../../../../services/AlertService';
import extendedTextCkeditorSelector
    from '../../../../../selectors/question/interactions/extendedTextInteraction/ckeditor/ExtendedTextCkeditorSelector';
import ckeditorService from '../../../../../services/CKEditorService';
import extendedTextKeyboardService from '../../../../../services/question/interactions/extendedTextInteraction/ExtendedTextKeyboardService';
import extendedTextA11ySelector from '../../../../../selectors/question/interactions/extendedTextInteraction/ExtendedTextA11ySelector';
import DrawingService from '../../../../../services/DrawingService';
import globalsService from '../../../../../services/GlobalsService';
import logService from '../../../../../services/LogService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../../services/a11yIIBScreenReaderService';
import { responseSendStatus } from '../../../../../services/ResponseSendingQueue';
import essayRestoreService, { essayRestoreKey }
    from '../../../../../services/question/interactions/extendedTextInteraction/EssayRestoreService';
import { CHANGE_CHECK_THROTTLE_MS } from '../../../../../constants/EssayRestoreConstants';


const editorTracker = {
    activeInstance: undefined,
};

function handleMathJaxWidgetDefinition(definition, editorInstance) {
    definition.data.defaults.math = '';
    definition.data.dialog = '';
    definition.data.edit = (data) => {
        editorInstance.mathEntryTriggerFunction(data.sender.element);
    };
}

function getWysiwygElement(editorInstance) {
    return editorInstance.container.$.getElementsByClassName('cke_wysiwyg_div')[0];
}

function setEditorHeightStyle(editorHeight, autoExpand, editorInstance) {
    const ckeContentsElement = getWysiwygElement(editorInstance);
    if (autoExpand) {
        ckeContentsElement.style.minHeight = `${editorHeight}px`;
    } else {
        ckeContentsElement.style.height = `${editorHeight}px`;
    }
}

function getEditorHeight(expectedLines) {
    return 16 + (22 * expectedLines); // padding + estimate paragraph height.
}

function ensureAutogrowPluginDefined(CKEDITOR_GLOBAL, autoExpand) {
    if (autoExpand && CKEDITOR_GLOBAL.config.extraPlugins.indexOf('autogrow') === -1) {
        CKEDITOR_GLOBAL.config.extraPlugins = `${CKEDITOR_GLOBAL.config.extraPlugins},autogrow`;
    }
}

function disableBrowserNativeTableEditing() {
    document.execCommand('enableInlineTableEditing', false, 'false');
}

export function shouldIgnoreWrapperKeyDown(key) {
    return key === 'Tab';
}

/**
 * Returns true if the wrapper keydown event should be handled (i.e. should call onDocumentClicked).
 * - Always ignores Tab (shouldIgnoreWrapperKeyDown).
 * - Always ignores events inside the editable area (let CKEditor handle them).
 * - Ignores events inside the toolbar UNLESS the key is Escape (Esc must exit toolbar back to editor).
 */
export function shouldHandleWrapperKeyDown(key, target) {
    if (shouldIgnoreWrapperKeyDown(key) || isInsideCkeEditable(target)) {
        return false;
    }
    if (isInsideCkeToolbox(target) && key !== 'Escape') {
        return false;
    }
    return true;
}

/**
 * Returns true if the DOM node is inside a CKEditor toolbar container (.cke_toolbox).
 * Uses manual parent traversal instead of Element.prototype.closest for broad compatibility.
 */
function isInsideCkeToolbox(target) {
    let node = target;
    let depth = 0;
    while (node && depth < 10) {
        if (typeof node.className === 'string' && (` ${node.className} `).indexOf(' cke_toolbox ') !== -1) {
            return true;
        }
        node = node.parentNode;
        depth++;
    }
    return false;
}

/**
 * Returns true if the DOM node is inside the CKEditor editable content area (.cke_wysiwyg_div).
 * Uses manual parent traversal instead of Element.prototype.closest for broad compatibility.
 */
function isInsideCkeEditable(target) {
    let node = target;
    let depth = 0;
    while (node && depth < 10) {
        if (typeof node.className === 'string' && (` ${node.className} `).indexOf(' cke_wysiwyg_div ') !== -1) {
            return true;
        }
        node = node.parentNode;
        depth++;
    }
    return false;
}

export function setEditAreaA11yLabels(editorInstance, contentRevisionId, isA11yV5Enabled) {
    const wysiwygElement = getWysiwygElement(editorInstance);
    wysiwygElement.setAttribute('title', '');
    wysiwygElement.setAttribute('aria-label', '');

    if (!isA11yV5Enabled) {
        return;
    }

    // Suppress CKEditor's 'Text field' voice label on the application container
    const applicationContainer = editorInstance?.container?.$;
    if (applicationContainer) {
        applicationContainer.removeAttribute('aria-labelledby');
    }

    const questionContentId = contentRevisionId ? `questionText_${contentRevisionId}` : undefined;
    const promptId = contentRevisionId ? `interaction_prompt_${contentRevisionId}` : undefined;
    const labelledBy = [questionContentId, promptId].filter(Boolean).join(' ');

    if (labelledBy) {
        wysiwygElement.setAttribute('aria-labelledby', labelledBy);
    }
}

function getCkeditorPreRenderValue() {
    // Init MathJax plugin used by CKEditor
    return '<p><span class="math-tex" data-mathentryvalue="x">\\(x\\)</span></p>';
}

async function setEditorValue(editorInstance, value, isPreRender) {
    return new Promise((resolve, reject) => {
        const ckeditorInitValue = isPreRender ? getCkeditorPreRenderValue() : value;
        editorInstance.setData(ckeditorInitValue, {
            async callback() {
                try {
                    // CKEditor plugin handles math render in iframe
                    // await MathJaxDisplay.showMathJax(editorInstance.editable().$);
                    editorInstance.resetUndo();
                    resolve();
                } catch (error) {
                    reject(error);
                }
            },
        });
    });
}

function getEditorLanguage() {
    const language = sessionReader.getLanguage();
    return i18nService.languageMapIetfToCKEditor[language] || 'en';
}

/** Non-text elements that still count as an answer (drawings, tables, media). */
const NON_TEXT_CONTENT_TAGS = new Set(['IMG', 'TABLE', 'IFRAME', 'OBJECT', 'SVG', 'VIDEO', 'AUDIO', 'HR']);

/** True when the HTML has no visible answer, e.g. `<p>&nbsp;</p>` or `<p><br></p>`. */
export function isBlankEditorHtml(html) {
    if (typeof html !== 'string' || html.length === 0) {
        return true;
    }

    const { body } = new DOMParser().parseFromString(html, 'text/html');
    if (!body) {
        return true;
    }
    // tagName, not a selector list: jsdom does not match `svg` in one.
    const elements = Array.from(body.querySelectorAll('*'));
    if (elements.some(element => NON_TEXT_CONTENT_TAGS.has(element.tagName.toUpperCase()))) {
        return false;
    }

    return body.textContent.trim().length === 0;
}

class ExtendedTextCkeditorClass extends React.Component {
    static propTypes = {
        autoExpand: PropTypes.bool,
        autoSave: PropTypes.func.isRequired,
        forceSave: PropTypes.func, // Persists editor content regardless of dirty state
        isPreRender: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired,
        questionModel: PropTypes.object.isRequired,
        services: PropTypes.object,
        shouldForbidCopyPasteButtons: PropTypes.bool.isRequired,
        shouldSaveOnEmptyChange: PropTypes.bool.isRequired, // eslint-disable-line react/no-unused-prop-types
        userData: PropTypes.string, // The previously stored data
    };

    constructor(props) {
        super(props);

        this.state = {
            areSketchesEnabled: this.areSketchesEnabled(false),
            currentDrawingTarget: false,
            editorInstanceName: `editor_${stringUtils.getUID()}`,
            editorInstanceReady: false,
            isEmpty: !props.userData || props.userData.length === 0,
            selectedMathElement: undefined,
            showDrawingConfirmDeleteModal: false,
            showDrawingTool: false,
            showImageDrawingIcons: false,
            showMathEntryTool: false,
            showSnapshotsTool: false,
            spellCheckerDisabled: this.isSpellCheckerDisabled(false),
        };

        this.imageDrawingIconsRef = React.createRef();
    }

    async componentDidMount() {
        const instanceName = this.state.editorInstanceName;
        if (!this.isSpellCheckerDisabled(false)) {
            try {
                spellcheckerConfig.configureWProofReaderGeneralConfig();
                await CKEditorLoader.loadSpellcheckerBundle();
            } catch (e) {
                console.error('Failed initializing WProofReader', e);
            }
        }
        CKEditorLoader
            .init()
            .then(() => {
                this.initCKEditor(instanceName, this, window.CKEDITOR);
            });
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { services, questionModel } = this.props;

        if (this.isEditorInitialized() && nextProps.isReadOnly !== this.props.isReadOnly) {
            try {
                if (services.questionSelectionService.isQuestionInSectionWithSelectionEnabled(questionModel.contentRevisionId)) {
                    this.ckeditor.setReadOnly(nextProps.isReadOnly);
                }
            } catch (e) {
                reportError(`Error toggling CKEditor readOnly state. ${questionModel.contentRevisionId}`);
            }
        }
        this.areSketchesEnabled(true, () => {
            this.updateSketchesButtonState();
        });
    }

    componentDidUpdate(prevProps) {
        this.syncEssayRestoreRegistration(prevProps);
        this.applyLateUserData(prevProps);
    }

    /** isReadOnly can change at runtime (selectable questions); keep the registration in step. */
    syncEssayRestoreRegistration = (prevProps) => {
        if (this.props.isPreRender || prevProps.isReadOnly === this.props.isReadOnly) {
            return;
        }

        if (this.isEssayRestoreActive() && this.isEditorInitialized()) {
            const key = this.essayRestoreKey();
            essayRestoreService.registerEditor(key, this.getEssayRestoreAdapter());
            // A blank editor with a backup is offered straight away.
            essayRestoreService.handleContentChange(key);
        } else {
            essayRestoreService.unregisterEditor(this.essayRestoreKey());
        }
    };

    componentWillUnmount() {
        this.hasUnmounted = true;

        // Settle a pending restore write; its callback will never fire now.
        if (this.settleEssayRestoreWrite) {
            this.settleEssayRestoreWrite();
            this.settleEssayRestoreWrite = undefined;
        }

        clearTimeout(this.essayRestoreTrailingCheck);
        this.essayRestoreTrailingCheck = undefined;

        // Keeps the backup. isPreRender only: a read-only editor must still release its adapter.
        if (!this.props.isPreRender) {
            essayRestoreService.unregisterEditor(this.essayRestoreKey());
        }

        // remove ckeditor editor instance
        if (this.isEditorInitialized()) {
            if (CKEDITOR.dialog.getCurrent() &&
                this.ckeditor === CKEDITOR.dialog.getCurrent().getParentEditor()) {
                // if dialogs are open, unmount will not work
                CKEDITOR.dialog.getCurrent().hide();
            }

            this.ckeditor.focusManager.blur(true); // This is to fix a bug scheduled to be fixed in CKEditor 4.7.0.
            this.ckeditor.destroy();
            // So isEditorInitialized() is false for a destroyed editor.
            this.ckeditor = undefined;
        }
    }

    createEditorInstance(CKEDITOR_GLOBAL, instanceName, instanceConfig) {
        CKEDITOR_GLOBAL.replace(instanceName, instanceConfig);
        return CKEDITOR_GLOBAL.instances[instanceName];
    }

    initCKEditor = (instanceName, componentInstance, CKEDITOR_GLOBAL) => {
        if (!document.getElementById(instanceName)) {
            return; // The user has probably navigated away
        }

        // Disable internal user agent check in CKEditor that did not handle IEP
        CKEDITOR_GLOBAL.env.isCompatible = true;

        const {
            expectedLines, handleInteractionReady, isViewMediaPrint, isReadOnly, showWordCount, maxWords, autoExpand, userData,
            model, questionModel, isPreRender, shouldSaveOnEmptyChange,
        } = componentInstance.props;
        const debugEssayErasing = true; // essay erasing issue, see IB-3008
        const onChangeListenerRequired = shouldSaveOnEmptyChange || debugEssayErasing;
        const editorHeight = getEditorHeight(expectedLines);
        let editorToolbar = model.editorToolbar;
        let editorSpecialChars;
        if (settingsReader.isEssayToolbarPresetEnabled() && questionReader.hasEssayToolbarPreset(questionModel)) {
            editorToolbar = questionReader.getEssayToolbarPreset(questionModel);
        }
        if (settingsReader.isEssaySpecialCharsEnabled() && questionReader.hasEssaySpecialCharsPreset(questionModel)) {
            editorSpecialChars = questionReader.getEssaySpecialCharsPreset(questionModel);
        }
        const instanceConfig = getEditorConfig(CKEDITOR_GLOBAL, {
            autoExpand,
            disabledMenuItems: sessionReader.isOfflineMode() ? ['ia-mathEntry-tool'] : undefined,
            editorHeight,
            isReadOnly,
            isViewMediaPrint,
            language: getEditorLanguage(),
            maxWords,
            showWordCount,
            editorToolbar,
            editorSpecialChars,
        });

        CKEDITOR_GLOBAL.placeholder = t('ckeditor.placeholderText');

        this.ckeditor = this.createEditorInstance(CKEDITOR_GLOBAL, instanceName, instanceConfig);
        ensureAutogrowPluginDefined(CKEDITOR_GLOBAL, autoExpand);

        this.ckeditor.drawingTriggerFunction = this.showDrawingTool;
        this.ckeditor.mathEntryTriggerFunction = this.showMathEntryTool;
        this.ckeditor.snapshotsTriggerFunction = this.showSnapshotsTool;

        registerDrawingPlugin(CKEDITOR_GLOBAL);
        registerFixedPositionPlugin(CKEDITOR_GLOBAL, instanceName, editorTracker);

        expandPlugin.register(CKEDITOR_GLOBAL);
        snapshotsPlugin.register(CKEDITOR_GLOBAL);
        wordCountPlugin.register(CKEDITOR_GLOBAL);
        if (!sessionReader.isOfflineMode()) {
            mathEntryPlugin.register(CKEDITOR_GLOBAL);
        }


        this.ckeditor.on('widgetDefinition', (definition) => {
            if (definition.data.name === 'mathjax') {
                return handleMathJaxWidgetDefinition(definition, this.ckeditor);
            }
            return true;
        });

        this.ckeditor.on('focus', () => {
            this.updateButtons();
            editorTracker.activeInstance = this.ckeditor;
        });

        this.ckeditor.on('blur', () => {
            if (editorTracker.activeInstance === this.ckeditor) {
                editorTracker.activeInstance = undefined;
            }
            if (DrawingService.isCollapsibleDrawingModalEnabled()) {
                this.setState({ showImageDrawingIcons: false });
            }
        });

        // Add accessibility keyboard handling if IIB screen reader support is enabled
        if (extendedTextA11ySelector.getKeyboardInstructions()) {
            const isA11yV6 = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
                A11yForIIBScreenReaderIteration.v6
            );

            if (isA11yV6) {
                // When v6 is on, Alt+T replaces Alt+F10 for toolbar focus.
                // Register Alt+T as a native CKEditor keystroke mapped to the
                // built-in 'toolbarFocus' command — the same command that
                // CKEditor uses for Alt+F10. This ensures the toolbar focus
                // works identically (proper keyboard navigation mode, etc.).
                // Key code: CKEDITOR.ALT (0x440000) + 84 ('T')
                this.ckeditor.setKeystroke(CKEDITOR_GLOBAL.ALT + 84, 'toolbarFocus');

                // Remove the built-in Alt+F10 → toolbarFocus mapping so that
                // Alt+F10 does nothing when v6 is on.
                // Key code: CKEDITOR.ALT (0x440000) + 121 (F10)
                this.ckeditor.setKeystroke(CKEDITOR_GLOBAL.ALT + 121, false);
            }

            this.ckeditor.on('key', (evt) => {
                // evt.data.domEvent is a CKEDITOR.dom.event wrapper; use .$ to get the native DOM event
                // so that event.key / event.altKey etc. are accessible in handleAccessibilityShortcuts
                const options = {
                    returnToEditorCallback: () => extendedTextKeyboardService.returnToEditor(this.ckeditor),
                    submitCallback: () => {
                        // Submit the CKEditor content
                        componentInstance.autoSave();
                    },
                };

                // When v6 is OFF, keep Alt+F10 → toolbar focus via the service as fallback.
                // When v6 is ON, Alt+T is handled by setKeystroke above and Alt+F10 is disabled.
                if (!isA11yV6) {
                    options.focusToolbarCallback = () => extendedTextKeyboardService.focusToolbar(this.ckeditor);
                }

                const handled = extendedTextKeyboardService.handleAccessibilityShortcuts(evt.data.domEvent.$, options);

                if (handled) {
                    evt.cancel();
                }
            });
        }

        if (onChangeListenerRequired) {
            this.ckeditor.on('change', () => {
                if (!this.isEditorInitialized()) {
                    return;
                }

                if (debugEssayErasing) {
                    logService.logEssayErasing(this.ckeditor, questionModel?.contentRevisionId);
                }

                if (this.isEssayRestoreActive() && this.shouldRunEssayRestoreCheck()) {
                    essayRestoreService.handleContentChange(this.essayRestoreKey());
                }

                if (shouldSaveOnEmptyChange) {
                    const isEditorEmpty = this.ckeditor.getData().length === 0;
                    if (this.state.isEmpty !== isEditorEmpty) {
                        this.setState({
                            isEmpty: isEditorEmpty,
                        });
                        this.props.autoSave();
                    }
                }
            });
        }

        if (DrawingService.isCollapsibleDrawingModalEnabled()) {
            this.ckeditor.on('contentDom', (e) => {
                e.editor.editable().on('click', (evt) => {
                    const target = evt.data.getTarget();
                    this.setState({ showImageDrawingIcons: false });
                    if (target.is('img') && target.hasClass('ia-drawing')) {
                        this.setState({ currentDrawingTarget: target.$ });
                        DrawingService.setDrawingIconsPosition(target, this.imageDrawingIconsRef.current);
                        this.setState({ showImageDrawingIcons: true });
                    }
                });
            });
        }

        this.ckeditor.on('instanceReady', async ({ editor }) => {
            await setEditorValue(editor, userData, isPreRender);
            setEditorHeightStyle(editorHeight, autoExpand, editor);

            disableBrowserNativeTableEditing();
            const isA11yV5Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
                A11yForIIBScreenReaderIteration.v5
            );
            setEditAreaA11yLabels(editor, questionModel.contentRevisionId, isA11yV5Enabled);

            this.setEditorInstanceReady();
            this.updateButtons();
            if (!this.isSpellCheckerDisabled(false)) {
                spellcheckerConfig.configureWProofReaderOnInstanceReady(editor, questionModel.contentRevisionId);
            }
            handleInteractionReady(true);

            // instanceReady is async; the editor may already be unmounted.
            if (this.isEssayRestoreActive() && !this.hasUnmounted) {
                const key = this.essayRestoreKey();
                // Seed from the stored answer first, in case setData left the editor blank.
                const storedAnswer = this.props.userData;
                if (!isBlankEditorHtml(storedAnswer)) {
                    essayRestoreService.registerEditor(key, {
                        getContent: () => storedAnswer,
                        setContent: () => {},
                        saveContent: () => false,
                    });
                }
                essayRestoreService.registerEditor(key, this.getEssayRestoreAdapter());
                essayRestoreService.handleContentChange(key);
            }

            if (this.props.shouldForbidCopyPasteButtons) {
                ckeditorService.forbidCopyPasteButtons(editor);
            }
        });
    };

    essayRestoreKey = () => essayRestoreKey(this.props.questionModel.contentRevisionId, this.props.model.identifier);

    /** Per call: isReadOnly can change. A pre-render instance must never register. */
    isEssayRestoreActive = () => essayRestoreService.isEnabled()
        && !this.props.isReadOnly
        && !this.props.isPreRender;

    /** Cheap DOM check, unlike getData(). */
    hasVisibleEditorText = () => {
        try {
            const editable = this.ckeditor.editable();
            return Boolean(editable?.$ && editable.$.textContent.trim().length > 0);
        } catch (e) {
            return false; // cannot tell - let the authoritative check run
        }
    };

    /** Throttles detection while typing; a blank editor is always checked at once. */
    shouldRunEssayRestoreCheck = () => {
        if (!this.hasVisibleEditorText()) {
            return true;
        }

        const now = Date.now();
        const sinceLastCheck = now - (this.lastEssayRestoreCheckAt || 0);
        if (sinceLastCheck < CHANGE_CHECK_THROTTLE_MS) {
            this.scheduleTrailingEssayRestoreCheck(CHANGE_CHECK_THROTTLE_MS - sinceLastCheck);
            return false;
        }
        this.lastEssayRestoreCheckAt = now;
        return true;
    };

    /** One trailing pass after the throttle window, so the last edits are not missed. */
    scheduleTrailingEssayRestoreCheck = (delayMs) => {
        if (this.essayRestoreTrailingCheck) {
            return;
        }

        this.essayRestoreTrailingCheck = setTimeout(() => {
            this.essayRestoreTrailingCheck = undefined;
            if (this.hasUnmounted || !this.isEditorInitialized() || !this.isEssayRestoreActive()) {
                return;
            }
            this.lastEssayRestoreCheckAt = Date.now();
            essayRestoreService.handleContentChange(this.essayRestoreKey());
        }, delayMs);
    };

    /** getContent returns '' for visually empty markup. */
    getEssayRestoreAdapter = () => ({
        getContent: () => {
            if (!this.ckeditor || typeof this.ckeditor.getData !== 'function') {
                return undefined; // editor gone - cannot tell, so do not report a blanking
            }
            const html = this.ckeditor.getData();
            return isBlankEditorHtml(html) ? '' : html;
        },
        setContent: (html) => {
            // setData is async: saveContent waits for it. Unmount settles it via the stored resolver.
            this.essayRestoreWrite = new Promise((resolve) => {
                this.settleEssayRestoreWrite = resolve;
                this.ckeditor.setData(html, { callback: resolve });
            });
        },
        saveContent: isCurrent => Promise.resolve(this.essayRestoreWrite)
            .then(() => ((isCurrent && !isCurrent())
                ? responseSendStatus.FAILED
                : this.props.forceSave?.())),
    });

    /** Fills a stored answer that arrives after init, only into a blank, untouched editor. */
    applyLateUserData = (prevProps) => {
        const { userData } = this.props;

        if (userData === prevProps.userData || isBlankEditorHtml(userData)) {
            return false;
        }
        if (!this.isEditorInitialized() || this.isDirty()) {
            return false;
        }

        // getData() can throw on invalid content.
        let currentHtml;
        try {
            currentHtml = this.ckeditor.getData();
        } catch (e) {
            return false;
        }

        if (!isBlankEditorHtml(currentHtml)) {
            return false;
        }

        const key = this.essayRestoreKey();
        essayRestoreService.suppressDetection(key);
        this.ckeditor.setData(userData, {
            callback: () => {
                // The editor may have been destroyed meanwhile.
                if (this.hasUnmounted || !this.isEditorInitialized()) {
                    return;
                }
                this.ckeditor.resetUndo();
                essayRestoreService.closeAlert(key);
                essayRestoreService.captureContent(key);
            },
        });
        return true;
    };

    isSpellCheckerDisabled = (setState = true, setStateCompleteHandler = () => {}) => {
        const { interactionService } = this.props.services;
        const spellCheckerDisabled = !interactionService.isSpellCheckerEnabled();

        if (setState && (this.state.spellCheckerDisabled !== spellCheckerDisabled)) {
            this.setState({
                spellCheckerDisabled,
            }, setStateCompleteHandler);
        }

        return spellCheckerDisabled;
    };

    areSketchesEnabled = (setState = true, setStateCompleteHandler = () => {}) => {
        const { interactionService } = this.props.services;
        const areSketchesEnabled = interactionService.isSketchesEnabled();

        if (setState && (this.state.areSketchesEnabled !== areSketchesEnabled)) {
            this.setState({
                areSketchesEnabled,
            }, setStateCompleteHandler);
        }

        return areSketchesEnabled;
    };

    updateSketchesButtonState = () => {
        if (!this.isEditorInitialized()) {
            return;
        }

        const drawingIconElements = document.getElementsByClassName('drawing_icon');
        if (!drawingIconElements.length) {
            return;
        }

        const toolbarBtnElements = domService.getParentElementByClass(drawingIconElements[0], 'cke_toolbar');
        if (!toolbarBtnElements) {
            return;
        }

        if (this.state.areSketchesEnabled) {
            toolbarBtnElements.style.display = 'inline';
        } else {
            toolbarBtnElements.style.display = 'none';
        }
    };

    updateButtons = () => {
        this.updateSketchesButtonState();
    };

    onDocumentClicked = (e) => {
        const element = e.target;

        if (!this.isEditorInitialized()) {
            return;
        }

        if (DrawingService.isCollapsibleDrawingModalEnabled() && element.tagName === 'img') {
            this.setCurrentDrawing(this.state.currentDrawingTarget);
        } else {
            this.setCurrentDrawing(element);
        }

        if (this.ckeditor) {
            this.ckeditor.focus();
        }
    };

    onWrapperKeyDown = (e) => {
        if (!shouldHandleWrapperKeyDown(e.key, e.target)) {
            return;
        }
        this.onDocumentClicked(e);
    };

    setCurrentDrawing = (element) => {
        if (element && element.nodeType === 1 && element.className && element.className.includes('ia-drawing')) {
            this.currentDrawing = element;
            // this.showDrawingTool();
        } else {
            this.deleteCurrentSelectedDrawing();
        }
    };

    deleteCurrentSelectedDrawing = () => {
        delete this.currentDrawing;
    };

    setEditorInstanceReady = () => {
        this.setState({
            editorInstanceReady: true,
        });
    };

    getCkeditorStartUpHtml = () => {
        const { isPreRender } = this.props;
        const instanceName = this.state.editorInstanceName;
        const className = `ckeditor-container${isPreRender ? ' preRender' : ''}`;
        // Generate static html for ckeditor the DOM will be modified when starting up the editor
        return `<textarea 
                    class="${className}" 
                    data-questionId="${this.props.questionModel.contentRevisionId}"
                    data-interactionId="${this.props.model.identifier}"
                    name="${instanceName}" 
                    id="${instanceName}"
                    title="" 
                    aria-label="">
               </textarea>`;
    };

    getEditorData = () => {
        const { userData } = this.props;
        if (this.isEditorInitialized()) {
            try {
                const editorData = this.ckeditor.getData();
                this.ckeditor.resetDirty();
                return editorData.replace(/<pre[\s\S]*?<\/pre>|[\r\n]+/g, (match) => {
                    if (match.startsWith('<pre')) {
                        return match;
                    }
                    return '';
                });
            } catch (e) {
                console.error('Invalid content on save. Return last good value', e);
                alertService.showEssayEditorUnableToSaveAlert();
                // The backup is the last good value; userData may still be ''.
                return essayRestoreService.getBackupContent(this.essayRestoreKey()) || userData;
            }
        }
        // if editor is not initialized, return userData from state
        return userData;
    };

    /** @param {function} [onReset] runs once the blank is committed. */
    resetEditor = (onReset) => {
        const { questionModel } = this.props;
        if (this.isEditorInitialized()) {
            try {
                this.ckeditor.setData('', { callback: onReset });
            } catch (e) {
                reportError(`Error resetting CKEditor. ${questionModel.contentRevisionId}`);
            }
        }
    }

    isDirty = () => {
        let isDirty = false;
        if (this.isEditorInitialized()) {
            isDirty = this.ckeditor.checkDirty();
        }
        return isDirty;
    };

    isEditorInitialized = () => {
        return this.state.editorInstanceReady
            && this.ckeditor
            && typeof this.ckeditor.getData === 'function';
    };

    showDrawingTool = () => this.setState({ showDrawingTool: true });
    hideDrawingTool = () => this.setState({ showDrawingTool: false });

    showSnapshotsTool = () => this.setState({ showSnapshotsTool: true });
    hideSnapshotsTool = () => this.setState({ showSnapshotsTool: false });

    showMathEntryTool = mathElement => this.setState({ showMathEntryTool: true, selectedMathElement: mathElement });
    hideMathEntryTool = () => this.setState({ showMathEntryTool: false });

    showDrawingConfirmDeleteModal = () => this.setState({ showDrawingConfirmDeleteModal: true });
    hideDrawingConfirmDeleteModal = currentDrawingTarget => this.setState({
        showDrawingConfirmDeleteModal: false, currentDrawingTarget,
    });

    render() {
        const { questionModel } = this.props;
        let drawingHandlingWrapperClassName;
        let mathSymbolPalettePreset;

        if (settingsReader.isMathSymbolPalettePresetEnabled()) {
            mathSymbolPalettePreset = questionReader.getMathSymbolPalettePreset(questionModel);
        }
        if (DrawingService.isCollapsibleDrawingModalEnabled()) {
            drawingHandlingWrapperClassName =
                `${styles.iaDrawingHandlingWrapper} ${this.state.showImageDrawingIcons ? styles.showIaDrawingHandlingWrapper : ''}`;
        }

        let wrapperClass = `${styles.ckeditorWrapper} ckeditor-container-wrapper`;
        wrapperClass += this.props.autoExpand ? ' auto-expand' : ' scroll';

        const assetRoot = globalsService.getAssetRoot();
        let editPencilPath = 'images/drawing/editPencil.svg';
        let trashcanPath = 'images/drawing/trashcan.svg';
        if (assetRoot) {
            editPencilPath = `${assetRoot}/${editPencilPath}`;
            trashcanPath = `${assetRoot}/${trashcanPath}`;
        }

        const isA11yV5Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v5
        );
        const onKeyDownHandler = isA11yV5Enabled ? this.onWrapperKeyDown : undefined;

        return (
            <div className={styles.ckeditorRoot}>
                <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                    ref={(wrapperElement) => {
                        this.wrapper = wrapperElement;
                    }}
                    onClick={this.onDocumentClicked}
                    onKeyDown={onKeyDownHandler}
                    className={wrapperClass}
                    dangerouslySetInnerHTML={{ __html: this.getCkeditorStartUpHtml() }}
                />

                { DrawingService.isCollapsibleDrawingModalEnabled() &&
                    <div
                        ref={this.imageDrawingIconsRef}
                        className={drawingHandlingWrapperClassName}
                    >
                        <button
                            className={styles.iaDrawingHandlingButton}
                            onClick={this.showDrawingTool}
                        >
                            <img
                                src={editPencilPath}
                                alt={t('drawingTool.drawingEdit.placeholderAltText')}
                                className={styles.iaDrawingHandlingImg}
                            />
                        </button>
                        <button
                            className={styles.iaDrawingHandlingButton}
                            onClick={this.showDrawingConfirmDeleteModal}
                        >
                            <img
                                src={trashcanPath}
                                alt={t('drawingTool.drawingDelete.placeholderAltText')}
                                className={styles.iaDrawingHandlingImg}
                            />
                        </button>
                    </div>
                }

                { this.ckeditor &&
                    <DrawingToolCkeditorWrapper
                        ckeditor={this.ckeditor}
                        clickedDrawing={this.currentDrawing}
                        closeCallback={this.hideDrawingTool}
                        contentRevisionId={this.props.questionModel.contentRevisionId}
                        questionTitle={this.props.questionModel.title}
                        questionNr={this.props.questionModel.number}
                        store={storeManager.getStore()}
                        showDrawingTool={this.state.showDrawingTool}
                        showDrawingConfirmDeleteModal={this.state.showDrawingConfirmDeleteModal}
                        hideDrawingConfirmDeleteModal={this.state.hideDrawingConfirmDeleteModal}
                        deleteDrawing={this.deleteDrawing}
                    />
                }

                { this.state.showMathEntryTool &&
                    <MathEntryToolCkeditorModal
                        mathSymbolPalettePreset={mathSymbolPalettePreset}
                        mathElement={this.state.selectedMathElement}
                        ckeditor={this.ckeditor}
                        closeCallback={this.hideMathEntryTool}
                    />
                }

                { this.state.showSnapshotsTool &&
                    <SnapshotsModal
                        ckeditor={this.ckeditor}
                        closeCallback={this.hideSnapshotsTool}
                        interactionId={this.props.model.identifier}
                        questionId={this.props.questionModel.contentRevisionId}
                        store={storeManager.getStore()}
                    />
                }

                { this.state.showDrawingConfirmDeleteModal &&
                    <DrawingDeleteConfirmationModal
                        closeCallback={this.hideDrawingConfirmDeleteModal}
                        currentDrawingTarget={this.state.currentDrawingTarget}
                        deleteCurrentSelectedDrawing={this.deleteCurrentSelectedDrawing}
                    />
                }
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => extendedTextCkeditorSelector.selectProps(state, ownProps);
export default connect(mapStateToProps, null, null, { withRef: true })(ExtendedTextCkeditorClass);
